import {HTTPMethods} from '../types/enums';
import {uuidv4} from '../utils/createUuid';

import {LocalStorageOptions, LocalStorageType} from './API';

type ValueOrCollection<T> = T | T[] | {[key: string]: T};

export function requestLocal<T>(options: LocalStorageOptions, method: HTTPMethods, urlParams: {}, body: T): ValueOrCollection<T> {
    const id: string = options.type === LocalStorageType.VALUE ? undefined : (body && body['id']) || (urlParams || {})['id'];
    let result: ValueOrCollection<T>;

    switch (method) {
        case HTTPMethods.GET:
            result = readLocalStorage(options, id);
            break;
        case HTTPMethods.POST:
        case HTTPMethods.PUT:
            result = writeLocalStorage<T>(options, id, body);
            break;
        case HTTPMethods.DELETE:
            result = clearLocalStorage(options, id);
            break;
        default:
            throw new Error(`Invalid or unexpected method: ${method}`);
    }

    const transform = options.transform && options.transform[method];
    if (transform) {
        result = transform(result);
    }

    return result as unknown as T;
}

function readLocalStorage<T>(options: LocalStorageOptions, id: string | undefined): ValueOrCollection<T> {
    const item = localStorage.getItem(options.key);

    const storage = item && JSON.parse(item);
    switch (options.type) {
        case LocalStorageType.VALUE:
            if (item !== null) {
                return storage;
            } else {
                return getDefaultValue(options);
            }
        case LocalStorageType.ARRAY:
        {
            if (item === null) {
                return getDefaultValue(options);
            } else if (!Array.isArray(storage)) {
                throw new Error('Expected stored value to be an array');
            } else if (id === undefined) {
                return storage;
            }

            const index = storage.findIndex((entry) => entry.id === id);
            if (index === -1) {
                throw new Error('Resource not found.');
            }
            return storage[index];
        }
        case LocalStorageType.MAP:
            if (item === null) {
                return getDefaultValue(options);
            } else if (typeof storage !== 'object' || Array.isArray(storage) || !storage) {
                throw new Error('Expected stored value to be an object');
            } else if (id === undefined) {
                return storage;
            } else if (!storage.hasOwnProperty(id)) {
                throw new Error('Resource not found.');
            }

            return storage[id];
    }
}

function writeLocalStorage<T>(options: LocalStorageOptions, id: string, value: T): T {
    const {key} = options;
    const item = localStorage.getItem(key);
    let valueToStore: any;

    if (id === undefined && options.type !== LocalStorageType.VALUE) {
        // Likely an endpoint where the server generates an ID, create a random ID and append to `value`
        id = uuidv4();
        value['id'] = id;
    }

    switch (options.type) {
        case LocalStorageType.VALUE:
            valueToStore = value;
            break;
        case LocalStorageType.ARRAY:
            if (item === null) {
                valueToStore = [...getDefaultValue(options) as T[], value];
            } else {
                valueToStore = JSON.parse(item);

                if (!Array.isArray(valueToStore)) {
                    throw new Error('Expected stored value to be an array');
                } else {
                    const index = valueToStore.findIndex((entry) => entry.id === id);
                    if (index !== -1) {
                        valueToStore[index] = value;
                    } else {
                        valueToStore.push(value);
                    }
                }
            }
            break;
        case LocalStorageType.MAP:
            if (item === null) {
                valueToStore = {...getDefaultValue(options), [id]: value};
            } else {
                valueToStore = JSON.parse(item);

                if (typeof valueToStore !== 'object' || Array.isArray(valueToStore) || !valueToStore) {
                    throw new Error('Expected stored value to be an object');
                } else {
                    valueToStore[id] = value;
                }
            }
            break;
    }

    localStorage.setItem(key, JSON.stringify(valueToStore));

    return value;
}

function clearLocalStorage<T>(options: LocalStorageOptions, id: string): ValueOrCollection<T> {
    const {key} = options;
    const item = localStorage.getItem(key);
    let writeRequired: boolean = false;
    let storedValue: any;

    switch (options.type) {
        case LocalStorageType.VALUE:
            localStorage.removeItem(key);
            break;
        case LocalStorageType.ARRAY:
            if (item !== null) {
                storedValue = JSON.parse(item);

                if (!Array.isArray(storedValue)) {
                    throw new Error('Expected stored value to be an array');
                } else {
                    const index = storedValue.findIndex((entry) => entry.id === id);
                    if (index !== -1) {
                        storedValue.splice(index, 1);
                        writeRequired = true;
                    }
                }
            }
            break;
        case LocalStorageType.MAP:
            if (item !== null) {
                storedValue = JSON.parse(item);

                if (typeof storedValue !== 'object' || Array.isArray(storedValue) || !storedValue) {
                    throw new Error('Expected stored value to be an object');
                } else if (storedValue.hasOwnProperty(id)) {
                    delete storedValue[id];
                    writeRequired = true;
                }
            }
            break;
    }

    if (writeRequired) {
        localStorage.setItem(key, JSON.stringify(storedValue));
    }

    return storedValue;
}

function getDefaultValue<T>(options: LocalStorageOptions): ValueOrCollection<T> {
    const {type, default: defaultValue} = options;

    switch (type) {
        case LocalStorageType.VALUE:
            return defaultValue || null;
        case LocalStorageType.ARRAY:
            if (defaultValue === undefined) {
                return [];
            } else if (Array.isArray(defaultValue)) {
                return defaultValue;
            } else if (defaultValue !== null && defaultValue['id']) {
                return [defaultValue];
            } else {
                throw new Error(`Invalid default value for ${options.key}, not an array or identifiable value`);
            }
        case LocalStorageType.MAP:
            if (defaultValue === undefined) {
                return {};
            } else if (typeof defaultValue === 'object') {
                const isMap = Object.keys(defaultValue).every((key) => {
                    return defaultValue[key]['id'] === key;
                });

                if (isMap) {
                    return defaultValue;
                } else if (defaultValue.hasOwnProperty('id')) {
                    return {[defaultValue.id]: defaultValue};
                }
            }
            throw new Error(`Invalid default value for ${options.key}, not an map or identifiable value`);
    }
}
